import { FieldArray, FieldArrayRenderProps, getIn } from "formik"

import FormikMultiInputArray, { IMultiArrayEditProps } from "./FormikMultiInputArray"

interface IProps extends IMultiArrayEditProps {
  objectToAdd: any
}

/**
 * Entrypoint, in order to understand, how FormikMulitInputArray and FormikMulitFieldArray are working together.
 * Wrapping the component FormikMulitFieldArray, in order to generalize getting values, errors, removing element and adding an empty element
 * within a formik component.
 *
 * In order to do something like this,e.g.:
 * <FieldArray
    name="implementationLocations"
    component={LocationArray}
   />
 * We have to separate the FieldArrayRenderProps from our general component, the Array itself.
 * Why? So far, it was not possible simply, directly to add props within the FieldArray like a normal a Field,e.g.
             <FieldArray
              component={GeneralArrayWrapper}
              name="goals"
              label="process.goals"
              help="management:form.process.goals.help"
              placeholder="management:form.process.goals.placeholder"
              addButtonTitle="management:form.process.addGoal"
              values={values.goals}
            />
 * Therefor I have to define the FieldArrayRenderProps seperately from the specific props like placeholder, label... .
 *
 * How to add a new Array?
 * 1) Decide whether you need one InputField or more for one date, e.g. a locations needs just one InputField or a goal consists of the goal description itself as well as the measurement description, therfor needs two inputfields
 * => One inputfield: FormikSingleInputArray => FormikFieldArray
 * => Two or more inputfields: FormikMultiInputArray => FormikMultiFieldArray
 *
 *
 * Hint:
 * If you use it, within a formik component, dont exclude your field from showing in the general form error component. Why?
 * Cause,if an error occurs, formik will not focus the inputfield. Therefor, we need to display the error below/above the save button, so
 * the users see's immediately, that error occured.
 */

const FormikMultiFieldArray = ({
  name,
  objectToAdd,
  label,
  labelHelp,
  fieldsToShow,
  fields,
  onFocus,
  disabled,
  classNameForInputArrayBox,
  addButtonTitle,
  addButtonClassName: classNameForAddButton,
  addButtonSize,
  addButtonIconSize,
  removeActionIconSize,
  defaultType
}: IProps): JSX.Element => {

  return <FieldArray name={name}>
    {// Here you see the separation between FieldArrayRenderProps and our own component
      (fieldProps: FieldArrayRenderProps) => {
        // here, we merge them together => the FieldArrayRenderProps are used to inject the values, errors and interaction like adding or removing an element
        return <FormikMultiInputArray
          name={name}
          label={label}
          labelHelp={labelHelp}
          fieldsToShow={fieldsToShow}
          fields={fields}
          addButtonTitle={addButtonTitle}
          addButtonClassName={classNameForAddButton}
          classNameForInputArrayBox={classNameForInputArrayBox}
          addButtonSize={addButtonSize}
          addButtonIconSize={addButtonIconSize}
          removeActionIconSize={removeActionIconSize}
          values={getIn(fieldProps.form, ["values", name])}
          error={getIn(fieldProps.form, ["errors", name])}
          onFocus={onFocus}
          disabled={disabled}
          onAddEmptyElement={() => fieldProps.push(objectToAdd)}
          onRemoveElement={index => {
            // When remove is called and the error,e.g. of a location is just a string the error message will split into an array
            // reported Bug => https://github.com/jaredpalmer/formik/issues/3352
            // Therefor I remember all errors, remove the item and set the errors back to it's original state
            const errors = Object.assign({}, fieldProps.form.errors)
            fieldProps.remove(index)
            fieldProps.form.setErrors(errors)
            // validate the errors, so the errors are correctly mapped to the values
            setTimeout(() => void fieldProps.form.validateForm())
          }}
          defaultType={defaultType}
        />
      }
    }
  </FieldArray>
}
export default FormikMultiFieldArray